<template>
  <HeaderPanel />
  <ToastVue />
  <ConfirmDialog />
  <div class="utm-page">
    <div class="head">
      <div>
        <h1>UTM-конструктор</h1>
        <p>Соберите размеченную ссылку — метки подставятся сами.</p>
      </div>
      <div class="head-actions">
        <div class="seg">
          <button type="button" :class="{ on: mode === 'single' }" @click="mode = 'single'">Одна ссылка</button>
          <button type="button" :class="{ on: mode === 'multi' }" @click="mode = 'multi'">
            Мультисоздание <span class="tag">beta</span>
          </button>
        </div>
        <button type="button" class="btn" @click="dictionariesOpen = true">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 6h16M4 12h10M4 18h7"/></svg>
          Справочники
        </button>
      </div>
    </div>

    <UtmMulti v-if="mode === 'multi'" :origins="origins" :campaigns="campaigns" :popular="popularEvents" />
    <div v-else class="layout">
      <UtmBuilder :origins="origins" :campaigns="campaigns" :contents="contents" :popular="popularEvents" @change="onBuilderChange" />
      <UtmPreview
        ref="previewRef"
        :url="displayUrl"
        :base="built.base"
        :params="built.params"
        :plain="Boolean(linkOverride)"
        :status="status"
        :chips="chips"
        :campaign-text="campaignText"
        :event-text="draft.event?.event || ''"
        :registry-ready="Boolean(draft.event)"
        @copy="copyLink"
        @shorten="openShorten"
        @telegram="telegramOpen = true"
      />
    </div>
  </div>

  <UtmDictionaries
    :open="dictionariesOpen"
    :origins="origins"
    :campaigns="campaigns"
    :contents="contents"
    :loading="loading"
    @close="dictionariesOpen = false"
    @changed="loadAll"
  />

  <DialogVue v-model:visible="shortenOpen" modal header="Сократить ссылку" :style="{ width: '90vw', maxWidth: '600px' }">
    <LinkShortener :initial-url="shortenTarget" @link-created="onShortCreated" @error="onShortError" />
  </DialogVue>

  <DialogVue v-model:visible="telegramOpen" modal header="Ссылка для Telegram" :style="{ width: 'min(520px, 96vw)' }">
    <div class="tg">
      <label class="lbl" for="webAppLink">webAppLink</label>
      <input id="webAppLink" class="input" :value="webAppLink" placeholder="Латиница, необязательно" @input="onWebApp">
      <div class="tg-actions">
        <button type="button" class="btn primary" :disabled="telegramBusy" @click="createTelegram">Создать</button>
      </div>
      <div v-if="telegramLink" class="tg-result">
        <a :href="telegramLink" target="_blank" rel="noopener">{{ telegramLink }}</a>
        <button type="button" class="btn sm" @click="copyTelegram">Скопировать</button>
      </div>
    </div>
  </DialogVue>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import HeaderPanel from '@/components/HeaderPanel.vue'
import LinkShortener from '@/components/LinkShortener.vue'
import UtmBuilder from '@/components/utm/UtmBuilder.vue'
import UtmPreview from '@/components/utm/UtmPreview.vue'
import UtmMulti from '@/components/utm/UtmMulti.vue'
import UtmDictionaries from '@/components/utm/UtmDictionaries.vue'
import { utmApi } from '@/services/utmApi'
import { buildUtmUrl, mergePopular, transliterate, validateUrl } from '@/components/utm/utm-utils'
import { useUserStore } from '@/stores/user'

const toast = useToast()
const userStore = useUserStore()

const mode = ref('single')
const dictionariesOpen = ref(false)
const loading = ref(false)
const origins = ref([])
const campaigns = ref([])
const contents = ref([])
const popularServer = ref([])
const popularEvents = computed(() => mergePopular(origins.value, popularServer.value, 20))
const pruned = ref(false)
const linkOverride = ref('')
const previewRef = ref(null)
const shortenOpen = ref(false)
const shortenTarget = ref('')
const telegramOpen = ref(false)
const telegramBusy = ref(false)
const webAppLink = ref('')
const telegramLink = ref('')

const draft = reactive({
  protocol: 'https://',
  path: '',
  lead: '',
  origin: '',
  platform: '',
  event: null,
  fromEvent: {},
})
const utm = reactive({
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_content: '',
  utm_term: '',
})

const built = computed(() => buildUtmUrl({
  protocol: draft.protocol,
  path: draft.path,
  fields: utm,
  fromEvent: draft.fromEvent,
}))
const displayUrl = computed(() => linkOverride.value || built.value.url)
const status = computed(() => {
  if (!validateUrl(draft.path)) return 'url'
  if (!draft.event) return 'event'
  return 'ready'
})
const chips = computed(() => [draft.lead, draft.origin, draft.platform, draft.event?.event].filter(Boolean))
const campaignText = computed(() => {
  const value = utm.utm_campaign
  if (!value) return ''
  const found = campaigns.value.find((item) => item.value === value || item.name === value)
  if (found) return `${found.name} · ${found.value}`
  const folded = transliterate(value)
  return folded !== value ? `${value} · ${folded}` : value
})
const signature = computed(() => JSON.stringify({
  protocol: draft.protocol,
  path: draft.path,
  utm: { ...utm },
}))

watch(signature, (next, prev) => {
  if (prev && next !== prev) linkOverride.value = ''
})

function onBuilderChange(payload) {
  draft.protocol = payload.protocol
  draft.path = payload.path
  draft.lead = payload.lead
  draft.origin = payload.origin
  draft.platform = payload.platform
  draft.event = payload.event
  draft.fromEvent = payload.fromEvent || {}
  Object.assign(utm, payload.fields)
}

async function loadAll() {
  loading.value = true
  try {
    const [dataRes, campRes, contentRes, popularRes] = await Promise.all([
      utmApi.data(),
      utmApi.campaigns(),
      utmApi.content(),
      utmApi.popular(20).catch(() => null),
    ])
    origins.value = dataRes.data || []
    if (Array.isArray(popularRes?.data)) popularServer.value = popularRes.data
    campaigns.value = campRes.data || []
    const list = contentRes.data || []
    contents.value = list
    if (!pruned.value) {
      pruned.value = true
      const now = Date.now()
      const limit = 14 * 24 * 60 * 60 * 1000
      const stale = list.filter((item) => item.is_static == false && item.last_used_at && (new Date(item.last_used_at).getTime() + limit < now))
      for (const item of stale) {
        try {
          await utmApi.deleteContentLegacy(item.id)
          contents.value = contents.value.filter((row) => row.id !== item.id)
        } catch {
          /* автоочистку не показываем */
        }
      }
    }
  } catch {
    toast.add({ severity: 'error', summary: 'Не удалось загрузить справочники', life: 3500 })
  } finally {
    loading.value = false
  }
}

async function refreshPopular() {
  try {
    const res = await utmApi.popular(20)
    if (Array.isArray(res?.data)) popularServer.value = res.data
  } catch {
    /* частота не критична для сборки ссылки */
  }
}

function registryStamp() {
  const date = new Date()
  date.setHours(date.getHours() + 3)
  const pad = (value) => String(value).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${String(date.getFullYear()).slice(2)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function known(list, raw) {
  if (!raw || raw === 'none') return true
  const folded = transliterate(String(raw))
  return list.some((item) => item.value === raw || item.value === folded || item.name === raw)
}

async function rememberDictionaries() {
  const contentPayload = known(contents.value, utm.utm_content)
    ? null
    : { name: utm.utm_content, value: transliterate(utm.utm_content) }
  const campaignPayload = known(campaigns.value, utm.utm_campaign)
    ? null
    : { name: utm.utm_campaign, value: transliterate(utm.utm_campaign) }
  if (!contentPayload && !campaignPayload) return
  try {
    await utmApi.add({ utmContent: contentPayload, utmCampaign: campaignPayload })
    const [campRes, contentRes] = await Promise.all([utmApi.campaigns(), utmApi.content()])
    campaigns.value = campRes.data || []
    contents.value = contentRes.data || []
  } catch {
    /* как раньше: ошибка автодополнения не блокирует копирование */
  }
}

async function copyLink() {
  const text = displayUrl.value
  if (!text) {
    toast.add({ severity: 'warn', summary: 'Сначала укажите адрес', life: 2500 })
    return
  }
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    toast.add({ severity: 'error', summary: 'Не удалось скопировать', life: 2500 })
    return
  }
  previewRef.value?.remember(text)
  toast.add({ severity: 'success', summary: 'Ссылка скопирована', life: 2200 })
  if (!draft.event) {
    toast.add({ severity: 'warn', summary: 'Без события запись не создаётся', life: 3500 })
  } else {
    try {
      await utmApi.addRegistry({
        date: registryStamp(),
        username: userStore.user?.username || '',
        lead: draft.event.lead,
        platform: draft.event.platform,
        origin: draft.event.origin,
        event: draft.event.event,
        utm_campaign: utm.utm_campaign || '',
        link: text,
      })
    } catch {
      toast.add({ severity: 'warn', summary: 'Ссылка скопирована, в реестр записать не удалось', life: 3500 })
    }
  }
  await rememberDictionaries()
  await refreshPopular()
}

function openShorten() {
  shortenTarget.value = displayUrl.value
  shortenOpen.value = true
}
function onShortCreated(result) {
  if (result?.link) {
    linkOverride.value = result.link
    toast.add({ severity: 'success', summary: 'Короткая ссылка готова', life: 2200 })
  }
  shortenOpen.value = false
}
function onShortError() {
  toast.add({ severity: 'error', summary: 'Не удалось сократить ссылку', life: 3000 })
}
function onWebApp(event) {
  webAppLink.value = event.target.value.replace(/[^a-zA-Z0-9\s\-_]/g, '')
}
async function createTelegram() {
  const url = displayUrl.value
  if (!url) {
    toast.add({ severity: 'warn', summary: 'Сначала соберите ссылку', life: 2500 })
    return
  }
  telegramBusy.value = true
  try {
    const data = { url, botName: 'newgamebz_bot' }
    if (webAppLink.value.trim()) data.webAppLink = webAppLink.value.trim()
    const response = await utmApi.telegram(data)
    if (response.data?.success && response.data?.link) {
      telegramLink.value = response.data.link
    } else {
      telegramLink.value = ''
      toast.add({ severity: 'error', summary: 'Не удалось создать ссылку', life: 3000 })
    }
  } catch {
    toast.add({ severity: 'error', summary: 'Не удалось создать ссылку', life: 3000 })
  } finally {
    telegramBusy.value = false
  }
}
async function copyTelegram() {
  if (!telegramLink.value) return
  try {
    await navigator.clipboard.writeText(telegramLink.value)
    toast.add({ severity: 'success', summary: 'Ссылка для Telegram скопирована', life: 2000 })
  } catch {
    toast.add({ severity: 'error', summary: 'Не удалось скопировать', life: 2500 })
  }
}

function onKeydown(event) {
  if (mode.value !== 'single') return
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'c') return
  if (window.getSelection()?.toString()) return
  const tag = document.activeElement?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable) return
  event.preventDefault()
  copyLink()
}

onMounted(() => {
  loadAll()
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.utm-page {
  --text: #14181f;
  --muted: #6b7383;
  --muted-2: #98a0ae;
  --accent: #676B52;
  --accent-hover: #565a44;
  --accent-soft: #eef0e6;
  --border-strong: #d5d9e0;
  max-width: 1340px;
  margin: 0 auto;
  padding: 28px 32px 64px;
  background: #f8fafc;
  min-height: calc(100vh - 64px);
  color: var(--text);
  font-family: Ubuntu, 'Segoe UI', system-ui, sans-serif;
}
.head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 24px; }
.head h1 { margin: 0 0 6px; font-size: 26px; font-weight: 700; letter-spacing: -.02em; }
.head p { margin: 0; color: var(--muted); font-size: 14px; }
.head-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.seg { display: inline-flex; padding: 3px; background: #eceef2; border-radius: 11px; }
.seg button {
  border: 0; background: transparent; padding: 7px 14px; border-radius: 8px; font-size: 13px; font-weight: 500;
  color: var(--muted); display: inline-flex; align-items: center; gap: 7px; cursor: pointer; font-family: inherit;
}
.seg button.on { background: #fff; color: var(--text); box-shadow: 0 1px 2px rgba(16, 24, 40, .1); }
.tag {
  font-size: 10px; letter-spacing: .02em; text-transform: uppercase; background: var(--accent-soft); color: var(--accent);
  border-radius: 5px; padding: 1px 5px; font-weight: 700;
}
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; border-radius: 10px;
  border: 1px solid var(--border-strong); background: #fff; padding: 9px 14px; font-size: 13px; font-weight: 500;
  color: var(--text); cursor: pointer; font-family: inherit;
}
.btn:hover { border-color: #b9bfc9; background: #fbfbfc; }
.btn.primary { background: var(--accent); border-color: var(--accent); color: #fff; }
.btn.primary:hover { background: var(--accent-hover); border-color: var(--accent-hover); }
.btn.sm { padding: 6px 10px; font-size: 12px; border-radius: 8px; }
.icon { width: 16px; height: 16px; display: block; }
.layout { display: grid; grid-template-columns: minmax(0, 1fr) 396px; gap: 20px; align-items: start; }
.tg .lbl { display: block; font-size: 11px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: var(--muted-2); margin-bottom: 6px; }
.tg .input { width: 100%; height: 42px; border: 1px solid var(--border-strong); border-radius: 10px; padding: 0 12px; font-size: 14px; }
.tg-actions { margin-top: 12px; }
.tg-result { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
.tg-result a { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12.5px; color: var(--accent); word-break: break-all; }
@media (max-width: 1180px) {
  .layout { grid-template-columns: 1fr; }
}
@media (max-width: 760px) {
  .utm-page { padding: 16px 14px 40px; }
  .head { flex-direction: column; }
  .seg button, .btn, .tg .input { min-height: 44px; }
  .tg .input { font-size: 16px; }
}
</style>
